<template>
  <div v-if="shareableStep" :id="dropdownId" class="dropdown">
    <div :data-toggle="campaignLaunched ? 'dropdown' : null" v-tooltip.bottom="tooltip">
      <button
        class="btn btn-default dropdown-toggle"
        type="button"
        :id="buttonId"
        data-test="step-share-button"
        aria-haspopup="true"
        aria-expanded="true"
        :disabled="!campaignLaunched"
      >
        <span>Share</span>
        <span class="caret"></span>
      </button>
    </div>
    <div
      class="dropdown-menu dropdown-menu-right p-4"
      :class="{
        'facebook-hidden': !facebookShown,
        'twitter-hidden': !twitterShown,
        'linkedin-hidden': !linkedInShown,
        'email-hidden': !emailShown,
      }"
      :aria-labelledby="buttonId"
      @click="onDropdownMenuClick($event)"
    >
      <social-sharing :title="title" :url="step.link" network-tag="button" inline-template>
        <div class="share-buttons">
          <network network="facebook" class="facebook-share-button">
            <div class="share-icon-button">
              <i class="fab fa-facebook-f"></i>
            </div>
            <div>Facebook</div>
          </network>
          <network network="twitter" class="twitter-share-button">
            <div class="share-icon-button">
              <i class="fab fa-twitter"></i>
            </div>
            <div>Twitter</div>
          </network>
          <network network="linkedin" class="linkedin-share-button">
            <div class="share-icon-button">
              <i class="fab fa-linkedin-in"></i>
            </div>
            <div>LinkedIn</div>
          </network>
          <network network="email" class="email-share-button">
            <div class="share-icon-button">
              <i class="fas fa-envelope"></i>
            </div>
            <div>Email</div>
          </network>
        </div>
      </social-sharing>

      <div class="input-group mt-4">
        <input
          type="text"
          class="form-control step-share-input"
          :id="inputId"
          :value="step.link"
          :style="{width: `${step.link.length}ch`}"
          readonly
        />
        <span class="input-group-btn">
          <button
            class="btn btn-default"
            type="button"
            :disabled="copied"
            :title="copied ? 'The URL has already been copied!' : 'Copy the URL to clipboard'"
            @click="onCopyButtonClick"
          >
            {{ copied ? 'Copied!' : 'Copy' }}
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import {getCampaignStepPieces} from '../../../utility/api/campaign/getCampaignStepPieces';
import {convertServerDateToLocalMoment} from '../../../utility/date/date-converter';

const activityTypes = [
  'facebook-page-posts',
  'facebook-personal-posts',
  'twitter-personal-posts',
  'linkedin-personal-posts',
  'pages',
];

export default {
  name: 'step-share-dropdown',
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    editStatus: {
      type: String,
      required: true,
    },
    step: {
      type: Object,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      copied: false,
      stepPieces: null,
    };
  },
  computed: {
    shareableStep() {
      return this.step && this.step.link && activityTypes.includes(this.step.activity_type);
    },
    campaignLaunched() {
      return (
        this.campaign &&
        this.campaign.active &&
        this.campaign.launch &&
        convertServerDateToLocalMoment(this.campaign.launch).isBefore(moment())
      );
    },
    facebookShown() {
      return this.step && (this.step.activity_type === 'pages' || String(this.step.activity_type).includes('facebook'));
    },
    twitterShown() {
      return this.step && (this.step.activity_type === 'pages' || String(this.step.activity_type).includes('twitter'));
    },
    linkedInShown() {
      return this.step && (this.step.activity_type === 'pages' || String(this.step.activity_type).includes('linkedin'));
    },
    emailShown() {
      return this.step && this.step.activity_type === 'pages';
    },
    buttonId() {
      return `step-${this.step.id}-share-button`;
    },
    dropdownId() {
      return `step-${this.step.id}-share-dropdown`;
    },
    inputId() {
      return `step-${this.step.id}-share-input`;
    },
    title() {
      const titlePiece = (this.stepPieces || []).find(piece => piece.name === 'title');
      return titlePiece ? titlePiece.content : '';
    },
    tooltip() {
      if (this.campaignLaunched) {
        return 'Share';
      } else if (this.campaign && this.campaign.active) {
        const launchDate = convertServerDateToLocalMoment(this.campaign.launch, undefined, this.timezone).format('lll');
        const timeZone = String(this.timezone || moment.tz.guess()).replace('_', ' ');
        return `This campaign is due to launch on ${launchDate} (${timeZone}). You can share once it has launched.`;
      } else {
        return 'Launch the campaign in order to share.';
      }
    },
  },
  methods: {
    onCopyButtonClick() {
      if (window.navigator.clipboard) {
        window.navigator.clipboard.writeText(this.step.link).then(() => (this.copied = true));
      } else {
        document.getElementById(this.inputId).select();
        if (document.execCommand('copy')) {
          this.copied = true;
        }
      }
    },
    onDropdownMenuClick(event) {
      event.stopPropagation();
    },
    loadStepPieces() {
      if (!this.stepPieces) {
        getCampaignStepPieces(this.campaign.id, this.step.id).then(
          response => (this.stepPieces = response && response.data)
        );
      }
    },
  },
  mounted() {
    $(`#${this.dropdownId}`).on('show.bs.dropdown', () => this.loadStepPieces());
    $(`#${this.dropdownId}`).on('hidden.bs.dropdown', () => (this.copied = false));
  },
};
</script>

<style lang="scss">
.dropdown-toggle {
  padding: 5px 12px;
}

.share-buttons {
  display: flex;
  justify-content: center;

  & > button {
    all: unset;
    color: #000000;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 66px;

    &:hover > .share-icon-button {
      background-color: #000000;
    }
  }
}

.facebook-hidden .facebook-share-button {
  display: none;
}

.facebook-share-button > .share-icon-button {
  background-color: #1877f2;
}

.twitter-hidden .twitter-share-button {
  display: none;
}

.twitter-share-button > .share-icon-button {
  background-color: #1da1f2;
}

.linkedin-hidden .linkedin-share-button {
  display: none;
}

.linkedin-share-button > .share-icon-button {
  background-color: #2867b2;
}

.email-hidden .email-share-button {
  display: none;
}

.email-share-button > .share-icon-button {
    background-color: #888888;
}

.share-icon-button {
  border-radius: 50%;
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  width: 50px;
  margin-bottom: 8px;

  & > i {
    font-size: 24px;
  }
}

.step-share-input {
  font-size: 14px !important;
  height: 34px !important;
}
</style>
